// Shared Digital Products API client.
// Browser calls go same-origin through `/api/proxy` (HttpOnly cookie session).
// Artifact / icon URLs still resolve against the real API origin.

const UPSTREAM_API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/+$/, "") ?? "";

/** Same-origin BFF prefix used by the browser. */
const PROXY_BASE = "/api/proxy";

function apiFetchUrl(path: string): string {
  if (path.startsWith("/api/")) {
    // Auth BFF routes (login, logout, session, …) — not proxied.
    return path;
  }
  return `${PROXY_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Error shape thrown for any non-2xx API response. */
export class ApiError extends Error {
  code: string;
  status: number;
  fields?: Record<string, string[]>;
  /** Response `meta` when present (e.g. usage counters on 403 USAGE_LIMIT_EXCEEDED). */
  meta?: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string[]>,
    meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.meta = meta;
    // Required for reliable `instanceof` when Error subclasses are transpiled.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Duck-typed check — safer than bare `instanceof` across bundles. */
export function isApiError(err: unknown): err is ApiError {
  if (err instanceof ApiError) return true;
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: unknown; status?: unknown };
  return e.name === "ApiError" && typeof e.code === "string" && typeof e.status === "number";
}

/** True for free-tier / plan quota blocks from POST /v1/tool/{slug}/run. */
export function isUsageLimitExceeded(err: unknown): err is ApiError {
  if (!isApiError(err)) return false;
  if (err.code === "USAGE_LIMIT_EXCEEDED") return true;
  // Fallback if a proxy strips/renames the code but keeps the 403 + message.
  return (
    err.status === 403 &&
    /usage limit|quota exceeded|subscribe to continue/i.test(err.message)
  );
}

/** True for auth (and other) throttle responses — HTTP 429 / TOO_MANY_REQUESTS. */
export function isTooManyRequests(err: unknown): err is ApiError {
  if (!isApiError(err)) return false;
  if (err.code === "TOO_MANY_REQUESTS") return true;
  return err.status === 429 || /too many attempts/i.test(err.message);
}

type EnvelopeBody = {
  data?: unknown;
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
    details?: Record<string, string[]>;
  };
  meta?: Record<string, unknown>;
};

async function fetchEnvelope(
  path: string,
  init?: RequestInit,
): Promise<{ res: Response; body: EnvelopeBody | null }> {
  let res: Response;
  try {
    res = await fetch(apiFetchUrl(path), {
      ...init,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    // Let callers distinguish cancel from a real network failure.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    if (err instanceof Error && err.name === "AbortError") throw err;
    throw new ApiError(0, "NETWORK_ERROR", "Could not reach the server. Check your connection.");
  }

  if (res.status === 204) return { res, body: null };

  const body = (await res.json().catch(() => null)) as EnvelopeBody | null;
  return { res, body };
}

function firstDetailMessage(
  details: Record<string, string[]> | undefined,
): string | null {
  if (!details) return null;
  for (const messages of Object.values(details)) {
    const first = messages?.[0]?.trim();
    if (first) return first;
  }
  return null;
}

function throwFromEnvelope(res: Response, body: EnvelopeBody | null): never {
  const code = body?.error?.code ?? "UNKNOWN_ERROR";
  // OpenAPI validation uses `details`; some older shapes used `fields`.
  const fields = (body?.error?.details ?? body?.error?.fields) as
    | Record<string, string[]>
    | undefined;
  const message =
    body?.error?.message?.trim() ||
    firstDetailMessage(fields) ||
    "Something went wrong. Please try again.";
  throw new ApiError(res.status, code, message, fields, body?.meta);
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { res, body } = await fetchEnvelope(path, init);
  if (res.status === 204) return undefined as T;
  if (!res.ok) throwFromEnvelope(res, body);
  return body?.data as T;
}

/** Like `request`, but also returns the success envelope `meta`. */
export async function requestWithMeta<T, M = Record<string, unknown>>(
  path: string,
  init?: RequestInit,
): Promise<{ data: T; meta: M }> {
  const { res, body } = await fetchEnvelope(path, init);
  if (res.status === 204) return { data: undefined as T, meta: {} as M };
  if (!res.ok) throwFromEnvelope(res, body);
  return { data: body?.data as T, meta: (body?.meta ?? {}) as M };
}

/** Resolve a relative API path (e.g. artifact URL) against the API origin. */
export function resolveApiUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("/")) return `${UPSTREAM_API_BASE_URL}${pathOrUrl}`;
  return pathOrUrl;
}
